import { ChangeEvent, useState } from "react";
import React from "react";
import CloseIcon from "@mui/icons-material/Close";
import {
    Box,
    Button,
    Grid,
    TextField,
    Select,
    MenuItem,
    Typography,
    CardMedia,
    Input,
    SelectChangeEvent,
    Checkbox,
    FormControlLabel,
} from "@mui/material";
import dayjs from "dayjs";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useNavigate } from "react-router-dom";

type Weekday = "Mon" | "Tue" | "Wed" | "Thu" | "Fri" | "Sat" | "Sun";

interface AvailabilityPattern {
    days: Record<Weekday, boolean>;
    startTime: dayjs.Dayjs;
    endTime: dayjs.Dayjs;
}

const AddService = () => {
    const navigate = useNavigate();

    const [category, setCategory] = useState("default");
    const [subCategory, setSubCategory] = useState("default");
    const [priceType, setPriceType] = useState("hourly");
    const [images, setImages] = useState<(string | null)[]>(Array(6).fill(null));

    // Availability patterns
    const [availabilityPatterns, setAvailabilityPatterns] = useState<AvailabilityPattern[]>([
        {
            days: {
                Mon: false,
                Tue: false,
                Wed: false,
                Thu: false,
                Fri: false,
                Sat: false,
                Sun: false,
            },
            startTime: dayjs("09:00", "HH:mm"),
            endTime: dayjs("17:00", "HH:mm"),
        },
    ]);

    const handleDayToggle = (index: number, day: Weekday) => {
        const updated = [...availabilityPatterns];
        updated[index].days[day] = !updated[index].days[day];
        setAvailabilityPatterns(updated);
    };

    const handleTimeChange = (
        index: number,
        field: "startTime" | "endTime",
        newValue: dayjs.Dayjs | null
    ) => {
        const updated = [...availabilityPatterns];
        updated[index][field] = newValue || dayjs();
        setAvailabilityPatterns(updated);
    };

    const addAvailabilityPattern = () => {
        setAvailabilityPatterns([
            ...availabilityPatterns,
            {
                days: {
                    Mon: false,
                    Tue: false,
                    Wed: false,
                    Thu: false,
                    Fri: false,
                    Sat: false,
                    Sun: false,
                },
                startTime: dayjs("09:00", "HH:mm"),
                endTime: dayjs("17:00", "HH:mm"),
            },
        ]);
    };

    const removeAvailabilityPattern = (index: number) => {
        const updated = availabilityPatterns.filter((_, i) => i !== index);
        setAvailabilityPatterns(updated);
    };

    // Handle Image Upload
    const handleImageChange = (event: ChangeEvent<HTMLInputElement>, index: number) => {
        const file = event.target.files?.[0];
        if (file) {
            const imageUrl = URL.createObjectURL(file);
            const newImages = [...images];
            newImages[index] = imageUrl;
            setImages(newImages);
        }
    };

    const handleRemoveImage = (index: number) => {
        const newImages = [...images];
        newImages[index] = null;
        setImages(newImages);
    };

    return (
        <>
            <div className="main_title">
                <h1>Add Service</h1>
                <p>
                    <button onClick={() => navigate("/dashboard")}>Dashboard</button> -{" "}
                    <button onClick={() => navigate("/service-listing")}>Manage Services</button> - Add
                    Service
                </p>
            </div>

            <div className="cards custom_form_details">
                <div className="form">
                    <div className="gap_m w_100">
                        {/* Category */}
                        <div className="control_group w_33">
                            <label>Select Category</label>
                            <Select
                                fullWidth
                                value={category}
                                onChange={(e: SelectChangeEvent) => setCategory(e.target.value)}
                            >
                                <MenuItem disabled value="default">
                                    Select Category
                                </MenuItem>
                                <MenuItem value="home">Home Services</MenuItem>
                                <MenuItem value="repair">Repair</MenuItem>
                                <MenuItem value="fitness">Fitness</MenuItem>
                            </Select>
                        </div>

                        {/* Sub-category */}
                        <div className="control_group w_33">
                            <label>Select Sub-category</label>
                            <Select
                                fullWidth
                                value={subCategory}
                                onChange={(e: SelectChangeEvent) => setSubCategory(e.target.value)}
                            >
                                <MenuItem disabled value="default">
                                    Select Sub-category
                                </MenuItem>
                                <MenuItem value="cleaning">Cleaning</MenuItem>
                                <MenuItem value="plumbing">Plumbing</MenuItem>
                                <MenuItem value="training">Training</MenuItem>
                            </Select>
                        </div>

                        {/* Service Name */}
                        <div className="control_group w_33">
                            <label>Enter Service Name</label>
                            <TextField placeholder="Enter service name" fullWidth hiddenLabel />
                        </div>

                        {/* Description */}
                        <div className="control_group w_100">
                            <label>Enter Description of Service</label>
                            <TextField
                                fullWidth
                                multiline
                                minRows={4}
                                placeholder="Describe the service, inclusions, etc."
                            />
                        </div>

                        {/* Price */}
                        <div className="control_group w_33">
                            <label>Service Price</label>
                            <TextField placeholder="Enter price" fullWidth hiddenLabel />
                        </div>

                        {/* Price Type */}
                        <div className="control_group w_33">
                            <label>Price Type</label>
                            <Select
                                fullWidth
                                value={priceType}
                                onChange={(e: SelectChangeEvent) => setPriceType(e.target.value)}
                            >
                                <MenuItem value="hourly">Hourly</MenuItem>
                                <MenuItem value="fixed">Fixed</MenuItem>
                            </Select>
                        </div>

                        {/* Tax */}
                        <div className="control_group w_33">
                            <label>Tax (if applicable)</label>
                            <TextField placeholder="Enter tax percentage or value" fullWidth hiddenLabel />
                        </div>

                        {/* Duration / Time Slots */}
                        <div className="control_group w_50">
                            <label>Duration or Time Slots (if applicable)</label>
                            <TextField placeholder="E.g., 1 hour, 30 mins, 2 hours" fullWidth hiddenLabel />
                        </div>

                        {/* Max Bookings */}
                        <div className="control_group w_50">
                            <label>Set Maximum Bookings Per Slot/Day</label>
                            <TextField placeholder="Enter max bookings per slot/day" fullWidth hiddenLabel />
                        </div>

                        {/* Add-ons */}
                        <div className="control_group w_100">
                            <label>Add Optional Extras or Add-ons (Optional)</label>
                            <TextField
                                fullWidth
                                multiline
                                minRows={2}
                                placeholder="List optional extras (e.g., deep cleaning, express service)..."
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Availability Section */}
            <div className="cards availability_section">
                <Typography variant="h6" fontWeight={600} mb={2}>
                    Set Service Availability
                </Typography>

                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    {availabilityPatterns.map((pattern, index) => (
                        <Box
                            key={index}
                            border="1px solid #ddd"
                            borderRadius="8px"
                            padding="16px"
                            marginBottom="16px"
                        >
                            <Typography fontWeight={500} mb={1}>
                                Availability Pattern {index + 1}
                            </Typography>

                            <Grid container spacing={2}>
                                {/* Days of the week */}
                                <Grid item xs={12} sm={8}>
                                    <Box display="flex" flexWrap="wrap" gap={2}>
                                        {(Object.keys(pattern.days) as Weekday[]).map((day) => (
                                            <FormControlLabel
                                                key={day}
                                                control={
                                                    <Checkbox
                                                        checked={pattern.days[day]}
                                                        onChange={() => handleDayToggle(index, day)}
                                                    />
                                                }
                                                label={day}
                                            />
                                        ))}
                                    </Box>
                                </Grid>

                                {/* Time Pickers */}
                                <Grid item xs={12} sm={4}>
                                    <div className="form">
                                        <Box display="flex" gap={2}>
                                            <TimePicker
                                                label="Start Time"
                                                value={pattern.startTime}
                                                onChange={(val) => handleTimeChange(index, "startTime", val)}
                                            />
                                            <TimePicker
                                                label="End Time"
                                                value={pattern.endTime}
                                                onChange={(val) => handleTimeChange(index, "endTime", val)}
                                            />
                                        </Box>
                                    </div>
                                </Grid>
                            </Grid>

                            <Box mt={2}>
                                <Button color="error" onClick={() => removeAvailabilityPattern(index)}>
                                    Remove Pattern
                                </Button>
                            </Box>
                        </Box>
                    ))}

                    <Button variant="outlined" onClick={addAvailabilityPattern}>
                        + Add Another Weekly Pattern
                    </Button>
                </LocalizationProvider>
            </div>

            {/* Upload Section */}
            <div className="cards upload_section">
                <Typography variant="h6" fontWeight={600} mb={2}>
                    Upload Images or Videos
                </Typography>

                <Box display="grid" gridTemplateColumns="repeat(auto-fill, 120px)" gap={2} mt={1}>
                    {images.map((image, i) => (
                        <div key={i}>
                            {image ? (
                                <div className="upload_image">
                                    <div className="upload_image_holder square">
                                        <figure>
                                            <CardMedia component="img" image={image} alt={`upload-${i}`} />
                                        </figure>
                                        <CloseIcon onClick={() => handleRemoveImage(i)} />
                                    </div>
                                </div>
                            ) : (
                                <label className="upload_image" htmlFor={`icon-button-file-${i}`}>
                                    <Input
                                        sx={{ display: "none" }}
                                        id={`icon-button-file-${i}`}
                                        type="file"
                                        inputProps={{
                                            accept: "image/*,video/*",
                                        }}
                                    />
                                    <span className="upload_image_holder square">
                                        <figure className="add_img">
                                            <img src="/static/images/placeholder.png" alt="icon" />
                                            <span className="upload_img_icon">
                                                <img src="/static/images/edit2_icon.svg" alt="edit" />
                                            </span>
                                        </figure>
                                    </span>
                                </label>
                            )}
                        </div>
                    ))}
                </Box>

                <div className="w_100 mt_20">
                    <Button className="btn_primary" size="large">
                        Save Service
                    </Button>
                </div>
            </div>
        </>
    );
};

export default AddService;
